import React, { useContext, useEffect, useMemo, useState } from 'react'
import * as MicrosoftGraph from "@microsoft/microsoft-graph-types"

import { ConfigContext, MyUserDataContext } from '../contexts';
import { Loader, MultiSelect } from '@mantine/core';
import { ExternalGroup } from '../types';

type UserGroupsSearchProps = {
    values?: string[];
    label?: string;
    /** Passed back as the second argument of onItemSubmit so one handler can serve several pickers */
    objKey?: string | null;
    /** Graph endpoint to list from, e.g. "/groups/" or "/users/" */
    apiURl?: string | null;
    onItemSubmit?: (values: string[], objKey?: string) => void;
    /** Receives an id -> displayName map of every option offered (Graph + external) */
    onDataMapRetrieved?: (data: Record<string, string>) => void;
    disabled?: boolean;
};

type PickerOption = { value: string; label: string };

const GROUPS_ENDPOINT = /^\/?groups\/?$/i;

/**
 * Multi-select of directory objects. Options come from Microsoft Graph using the
 * signed-in user's token, which only ever sees that user's own tenant. For group
 * pickers we therefore also offer the groups stored in config/externalGroups
 * (e.g. KFH bank groups supplied by id), so a KFH Capital admin can map them.
 */
const UserGroupsSearch = (props: UserGroupsSearchProps) => {
    const { onItemSubmit, onDataMapRetrieved, values, objKey, apiURl, disabled } = props;
    const [graphResults, setGraphResults] = useState<MicrosoftGraph.DirectoryObject[]>([]);
    const [graphLoaded, setGraphLoaded] = useState<boolean>(false);
    const [chosenData, setChosenData] = useState<string[] | undefined>(values);
    const [autocompleteLoading, setAutocompleteLoading] = useState<boolean>(false);
    const appContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);

    const isGroupsPicker = !!apiURl && GROUPS_ENDPOINT.test(apiURl);
    const externalGroups: ExternalGroup[] = useMemo(
        () => (isGroupsPicker ? configContext.externalGroups?.groups ?? [] : []),
        [isGroupsPicker, configContext.externalGroups?.groups],
    );

    useEffect(() => {
        if (!apiURl?.length) {
            setGraphLoaded(true);
            return;
        }
        if (!appContext.microsoftClient) {
            // No Graph client (e.g. token not ready yet); external groups still render.
            setGraphLoaded(true);
            return;
        }
        setAutocompleteLoading(true);
        appContext.microsoftClient
            .api(apiURl)
            .header("ConsistencyLevel", "eventual")
            .top(999)
            .get()
            .then((res) => {
                setGraphResults((res?.value ?? []) as MicrosoftGraph.DirectoryObject[]);
            })
            .catch((e: unknown) => {
                console.error("UserGroupsSearch: Graph request failed", apiURl, e);
            })
            .finally(() => {
                setGraphLoaded(true);
                setAutocompleteLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiURl, appContext.microsoftClient]);

    const options: PickerOption[] = useMemo(() => {
        const seen = new Set<string>();
        const items: PickerOption[] = [];
        for (const obj of graphResults) {
            const item = obj as MicrosoftGraph.Group | MicrosoftGraph.User;
            if (!item.id || seen.has(item.id)) continue;
            seen.add(item.id);
            items.push({ value: item.id, label: item.displayName ?? item.id });
        }
        for (const group of externalGroups) {
            if (!group.id || seen.has(group.id)) continue;
            seen.add(group.id);
            items.push({ value: group.id, label: `${group.displayName} (${group.tenant})` });
        }
        return items;
    }, [graphResults, externalGroups]);

    // Report the id -> name map once Graph has answered (and again if the
    // external list changes), so callers can label ids they store.
    useEffect(() => {
        if (!graphLoaded || !onDataMapRetrieved) return;
        const idDisplayNameMap: Record<string, string> = {};
        for (const option of options) {
            idDisplayNameMap[option.value] = option.label;
        }
        onDataMapRetrieved(idDisplayNameMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graphLoaded, options]);

    const handleChange = (selected: string[]) => {
        // Keyed callers get (values, key) so one handler can serve several pickers.
        onItemSubmit?.(selected, objKey ?? undefined);
    }

    useEffect(() => {
        setChosenData(values)
    }, [values])


    return (
        <>
            <MultiSelect style={{ height: "100%" }}
                data={options}
                value={chosenData}
                disabled={disabled}
                searchable
                onChange={handleChange}
                comboboxProps={{ withinPortal: true }}
                placeholder={`Choose ${props.label || ""}`}
                label={props.label || ""}
            />
            <Loader
                size="xs"
                style={() => ({
                    width: "50px",
                    maxWidth: "50px",
                    visibility: autocompleteLoading ? "visible" : "hidden",
                })}
            />
        </>
    )
}

export default UserGroupsSearch
